// ログイン後の共通処理（チャット・ログインボーナス・中断戦闘・購入の反映待ち・同時接続の ping）とログアウト。
import { api, devAuth } from './api';
import { autoRun, autoRunElsewhere, haulOfInterrupted, haulOfSettled, stopAutoRun, takeInterruptedRun } from './autorun';
import { clearSaved, loadSaved } from './battle/controller';
import { powerOf } from './core-bridge';
import { levelNow, patch, refreshMe, state, tickStamina } from './state';
import { playHaul } from './ui/haul-fx';
import { playOmikuji } from './ui/omikuji-fx';
import { startChat, stopChat } from './ui/chat';
import { showLoginBonus } from './ui/login-bonus';
import { confirmDialog, toast } from './ui/overlay';
import { newRecruits, showRecruits } from './ui/recruit';
import { trackPurchase } from './ui/x-pixel';

let pingTimer: ReturnType<typeof setInterval> | null = null;
let clockTimer: ReturnType<typeof setInterval> | null = null;
document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && tickStamina());

/** skipBonus：ログインボーナスの札を出さない（初回の流れで初戦の前に札を重ねないため。TOP を開いたときに出す） */
export async function onLoggedIn(opts: { skipBonus?: boolean } = {}) {
  // オート周回のまま閉じていた：途中の1戦はサーバーが放棄して猶予の周回に置き換えるので、「戦闘に戻る」は出さない
  // （同じブラウザの別タブで周回が動いているときは触らない）
  const farmClosed = !!state.profile?.farm?.active && !autoRunElsewhere();
  if (farmClosed) clearSaved();
  const s = loadSaved();
  patch({ suspended: s ? { stageId: s.stageId, charId: s.charId, savedAt: s.savedAt } : null });
  startChat();
  if (!opts.skipBonus && state.me && !state.me.loginBonus?.claimedToday) void showLoginBonus();
  resumePendingOrder();
  void settleFarm();
  // 同時接続の計測用（表示中のタブだけ、約4分ごと）
  // 兵糧の表示を時間で進める（同じ画面にいても増える）。画面に戻ったときもすぐ
  clockTimer ??= setInterval(() => tickStamina(), 10_000);
  pingTimer ??= setInterval(() => {
    if (document.visibilityState === 'visible' && state.me) void api.ping().catch(() => {});
  }, 240_000);
}
// 戻ってきたら確かめる（ほかの端末で開かれていたら replaced で閉じる）
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state.me && pingTimer) void api.ping().catch(() => {});
});

/** ほかの端末で開かれた：この端末のオート周回・チャット・ping を止める（周回はあちらの端末が受け取る） */
export function onReplaced() {
  if (autoRun()) stopAutoRun('ほかの端末で開かれたため停止しました');
  stopChat();
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
}

/**
 * オート周回のまま閉じていた：最後の1戦から10分までの周回をサーバーが計算して渡すので、受け取って、見るか聞いてからまとめ演出を出す（GAME_DESIGN 20章）
 */
async function settleFarm() {
  const f = state.profile?.farm;
  if (autoRunElsewhere()) return;
  // 再読み込み前の周回の集計（端末に保存してある）があれば、閉じていたあいだの分と合わせて全戦のまとめにする
  const prev = takeInterruptedRun();
  // pending：閉じて10分たったところでサーバーが先に計算して通知した分
  const due = !!(f?.active || f?.pending);
  if (!due && !prev) return;
  const before = { level: levelNow(), power: powerOf(state.profile, f?.charId ?? prev!.cfg.charId) };
  try {
    const res = due ? await api.farmSettle() : null;
    if (!res?.battles && !prev) return;
    await refreshMe().catch(() => {});
    // 閉じていたあいだに式神が仲間になった：まとめ（リザルト）の前に小窓
    if (res) await showRecruits(newRecruits(res.runs.map((x) => x.rewards)));
    const h = prev ? haulOfInterrupted(prev, res) : haulOfSettled(res!, before);
    if (h && (await confirmDialog('オート周回が中断されました。\nリザルトを見ますか？', { ok: '見る', cancel: '閉じる', title: 'オート周回' }))) await playHaul(h);
  } catch {
    /* 受け取れなくても先に進める（次に開いたときにもう一度） */
  }
}

export async function logout() {
  try {
    await api.logout();
  } catch {
    /* セッションが切れていても画面はログアウトする */
  }
  devAuth.set(null);
  try {
    sessionStorage.removeItem('hk.mock');
  } catch {
    /* 無視 */
  }
  window.google?.accounts.id.disableAutoSelect();
  stopChat();
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
  patch({ me: null, resources: {}, entitlements: new Set(), items: {}, stamina: null, profile: null, progress: null });
  location.hash = '#/login';
}

// ---------- 購入の反映待ち（Stripe から戻ったあと Webhook の反映を待つ） ----------
const ORDER_KEY = 'hk.pendingOrder';
export function setPendingOrder(id: string | null) {
  try {
    if (id) localStorage.setItem(ORDER_KEY, id);
    else localStorage.removeItem(ORDER_KEY);
  } catch {
    /* 無視 */
  }
  patch({ pendingOrder: id });
}
let polling = false;
let kick: (() => void) | null = null;
/** force=true で待ち時間を飛ばしてすぐ再照会 */
export function resumePendingOrder(force = false) {
  if (polling) {
    if (force) kick?.();
    return;
  }
  let id: string | null = null;
  try {
    id = localStorage.getItem(ORDER_KEY);
  } catch {
    /* 無視 */
  }
  if (!id) return;
  patch({ pendingOrder: id });
  polling = true;
  void (async () => {
    // 2秒 → 最大30秒間隔で10分間再照会（サーバーが Stripe に問い合わせる）。反映されたら /api/me を取り直す
    const until = Date.now() + 10 * 60_000;
    let wait = 2000;
    while (Date.now() < until && state.pendingOrder === id) {
      try {
        const { order } = await api.refreshOrder(id);
        if (order.status === 'paid') {
          trackPurchase(order.id, order.amount);
          await refreshMe();
          setPendingOrder(null);
          toast(`「${order.name}」が反映されました`, 'good', 3500);
          // おまもりガチャ10連・月パス：買ったときに引いた10連の演出
          if (order.items?.length) await playOmikuji(order.items);
          break;
        }
        if (order.status !== 'pending') {
          setPendingOrder(null);
          toast(order.status === 'expired' ? '購入は完了しませんでした' : '購入の状態が変わりました。購入履歴をご確認ください', 'info', 4000);
          break;
        }
      } catch {
        /* 通信失敗でも待ち続ける */
      }
      await new Promise<void>((r) => {
        const t = setTimeout(r, wait);
        kick = () => {
          clearTimeout(t);
          r();
        };
      });
      kick = null;
      wait = Math.min(30_000, wait * 1.6);
    }
    polling = false;
  })();
}
